import React, { useMemo } from "react";
import { prepareProductList } from "utils/product";
import ProductCard from "components/ProductCard";
import ProductCell from "components/ProductCell";
import SlimCTA from "components/SlimCTA";
import Pill from "components/Pill";
import { useProductsPrices } from "utils/selectors";
import ListViewWrapper from "components/ListViewWrapper";
import GridViewWrapper from "components/GridViewWrapper";
import Link from "next/link";
import Table from "components/Table";
import {
  BUTTON_TAGS,
  LIST_PRESENTATION,
  STATUS as UI_STATUS,
  TABLE_LIST_PRESENTATION_CONFIG,
  UI_THEME
} from "utils/constants/ui";
import { useRouter } from "next/router";
import { reduceActiveRowColumns } from "utils/table";
import startCase from "lodash/startCase";

const ProductGridView = ({ models, loadCount, customClasses }) => (
  <GridViewWrapper
    models={models}
    loadCount={loadCount}
    ModelComponent={ProductCell}
    customClasses={customClasses}
  />
);

const ProductListView = ({ models, loadCount, customClasses }) => (
  <ListViewWrapper
    models={models}
    loadCount={loadCount}
    ModelComponent={ProductCard}
    customClasses={customClasses}
  />
);

const LIST_COMPONENTS = {
  [LIST_PRESENTATION.LIST]: ProductListView,
  [LIST_PRESENTATION.GRID]: ProductGridView
};

const STATUS = "status";
const NAME = "name";
const PRICE_RANGE = "priceRange";
const CREATED_AT = "createdAt";

const HEADER_LABEL_MAP = {
  [NAME]: "Name",
  [STATUS]: "Status",
  [PRICE_RANGE]: "Price",
  [CREATED_AT]: "Created"
};

const COLUMN_KEYS = [NAME, PRICE_RANGE, STATUS, CREATED_AT];

const ProductList = () => {
  const router = useRouter();
  const { asPath } = router;
  const { products, prices } = useProductsPrices();

  const rows = useMemo(
    () =>
      prepareProductList({
        products,
        prices,
        shouldOrder: true
      }),
    []
  );

  const statusColumns = reduceActiveRowColumns(COLUMN_KEYS, rows);
  const COLUMNS_CONFIG = statusColumns.reduce((memo, key) => {
    const columnConfig = {
      uid: key,
      Header: HEADER_LABEL_MAP[key],
      accessor: key
    };
    if (key === STATUS) {
      columnConfig.Cell = (cellProps) => {
        return (
          <Pill
            customClasses={{
              container: "tc"
            }}
            copy={startCase(cellProps.value)}
            status={UI_STATUS.DEFAULT}
            theme={UI_THEME.SLIM}
          />
        );
      };
    }

    memo.push(columnConfig);
    return memo;
  }, []);

  const createActions = [
    {
      theme: "slim",
      copy: "Create",
      tag: BUTTON_TAGS.LINK,
      href: `${asPath}/create`
    }
  ];

  if (rows.length) {
    return (
      <Table
        scrollTopOnChange
        columnClick={(cell) => router.push(`${asPath}/${cell.row.original.id}`)}
        config={{
          search: {
            placeholder: "Search products"
          },
          columns: COLUMNS_CONFIG,
          presentation: TABLE_LIST_PRESENTATION_CONFIG,
          select: {
            fields: [STATUS]
          },
          actions: createActions,
          components: LIST_COMPONENTS,
          styles: {
            [NAME]: {
              width: 500
            },
            [STATUS]: {
              width: 100
            },
            [PRICE_RANGE]: {
              width: 160
            },
            [CREATED_AT]: {
              width: 150
            }
          },
          canClickColumn: {
            [NAME]: true,
            [STATUS]: true,
            [PRICE_RANGE]: true,
            [CREATED_AT]: true
          }
        }}
        rows={rows}
      />
    );
  } else {
    const ctaProps = {
      header: "No products",
      subheader: "Create one to get started selling.",
      actions: createActions
    };

    return (
      <div className="pa3">
        <SlimCTA {...ctaProps} />
      </div>
    );
  }
};

export default ProductList;
